import { Component, OnDestroy, OnInit } from '@angular/core';
import { ErrorParser } from '@app/classes/error-parser.class';
import { FeatureFlag } from '@app/enums';
import { BadgeOptions } from '@app/interfaces/badge-options.interface';
import { Model } from '@app/models/core/base.model';
import { BaseForm } from '@app/modules/forms/base.form';
import { PayrollModuleMenuService } from '@app/modules/payroll/services/payroll-module-menu.service';
import { AuthService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { NotifyService } from '@app/services/notify.service';
import { PayrollSettingsService, Settings } from '@app/services/payroll-settings.service';
import { TranslatableKey } from '@app/types/translatable.type';
import { Subscription } from 'rxjs';
import { Columns, Row } from '@app/components/simple-table/simple-table.component';
import { taxFormContactTableColumns } from '@payroll/meta/tax-form-contact-table.columns';
import { roeContactTableColumns } from '@payroll/meta/roe-contact-table.columns';

type ParsableError = Parameters<typeof ErrorParser.parse>[0];

@Component({
    styleUrls: ['./settings-show.style.scss'],
    templateUrl: './settings-show.template.html',
})
export class SettingsShowView extends BaseForm implements OnInit, OnDestroy {
    isLoading = true;
    companySettingsEditMode = false;
    companyTaxFormEditMode = false;

    settings?: Settings;
    originalSettings?: Settings;
    isActivateWorkersCompensationModalOpen = false;
    workersCompensationBadgeOptions?: BadgeOptions;
    canActivateWorkersCompensation = false;
    hasQcPayrollFeature = false;
    hasPayrollContactFlag = false;
    payrollEnabled = false;

    isTaxFormContactDialogOpen = false;
    isEditTaxFormContactMode = false;

    isRoeContactDialogOpen = false;
    isEditRoeContactMode = false;

    taxFormContactRows: Row[] = [];
    taxFormContactColumns: Columns[] = taxFormContactTableColumns;

    roeContactRows: Row[] = [];
    roeContactColumns: Columns[] = roeContactTableColumns;

    private subscriptions: Subscription[] = [];

    constructor(
        private notify: NotifyService,
        public settingsService: PayrollSettingsService,
        private auth: AuthService,
        private features: FeatureService,
        public payrollModuleMenuService: PayrollModuleMenuService
    ) {
        super();
    }

    get scheduleFrequency(): TranslatableKey {
        if (!this.settings?.schedule?.frequency) {
            return '';
        }

        return `payroll.settings.frequencies.${this.settings.schedule.frequency}`;
    }

    get scheduleDayOfTheWeek(): TranslatableKey {
        if (!this.settings?.schedule?.dayOfTheWeek) {
            return 'payroll.settings.na';
        }

        return `payroll.settings.daysOfTheWeek.${this.settings.schedule.dayOfTheWeek}`;
    }

    get days(): number[] | undefined {
        return this.settings?.schedule?.daysOfTheMonth;
    }

    get scheduleDaysOfTheMonth(): TranslatableKey {
        if (!this.days) {
            return 'payroll.settings.na';
        }
        if (this.days.length === 1) {
            return this.days[0].toString();
        }
        return 'payroll.settings.scheduleDaysOfTheMonth';
    }

    closeTaxFormContactDialog(): void {
        this.isTaxFormContactDialogOpen = false;
    }

    onTaxFormContactEdit(isEditDialogMode: boolean): void {
        this.isEditTaxFormContactMode = isEditDialogMode;
        this.isTaxFormContactDialogOpen = true;
    }

    closeRoeContactDialog(): void {
        this.isRoeContactDialogOpen = false;
    }

    onRoeContactEdit(isEditDialogMode: boolean): void {
        this.isEditRoeContactMode = isEditDialogMode;
        this.isRoeContactDialogOpen = true;
    }

    async ngOnInit(): Promise<void> {
        this.hasQcPayrollFeature = await this.features.has(FeatureFlag.qcPayroll);
        this.hasPayrollContactFlag = await this.features.has(FeatureFlag.payrollContact);

        this.subscriptions.push(
            this.settingsService.loading$.subscribe((isLoading) => {
                this.isLoading = isLoading;
            }),

            this.settingsService.settings$.subscribe((settings) => {
                this.settings = settings;
                if (
                    this.settings.roeContactFirstName &&
                    this.settings.roeContactLastName &&
                    this.settings.roeContactAreaCode &&
                    this.settings.roeContactPhoneNumber &&
                    this.settings.roeContactPhoneExtensionNumber
                ) {
                    this.roeContactRows = [
                        {
                            cells: [
                                this.settings.roeContactFirstName + ' ' + this.settings.roeContactLastName,
                                '(' + this.settings.roeContactAreaCode + ') ' + this.settings.roeContactPhoneNumber,
                                this.settings.roeContactPhoneExtensionNumber,
                            ],
                        },
                    ];
                }

                this.settings = settings;
                if (
                    this.settings.taxFormContactFirstName &&
                    this.settings.taxFormContactLastName &&
                    this.settings.taxFormContactEmail &&
                    this.settings.taxFormContactAreaCode &&
                    this.settings.taxFormContactPhoneNumber &&
                    this.settings.taxFormContactPhoneExtensionNumber
                ) {
                    this.taxFormContactRows = [
                        {
                            cells: [
                                this.settings.taxFormContactFirstName + ' ' + this.settings.taxFormContactLastName,
                                this.settings.taxFormContactEmail,
                                '(' +
                                    this.settings.taxFormContactAreaCode +
                                    ') ' +
                                    this.settings.taxFormContactPhoneNumber,
                                this.settings.taxFormContactPhoneExtensionNumber,
                            ],
                        },
                    ];
                }
            })
        );

        try {
            await this.settingsService.reload();
            this.originalSettings = { ...this.settings };
        } catch (e) {
            this.notify.error(e);
            this.isLoading = false;
        }

        this.payrollEnabled = this.auth.company.isPayrollSyncEnabled;
        this.canActivateWorkersCompensation = this.checkIfWorkersCompActivated();
        this.workersCompensationBadgeOptions = this.getWorkersCompensationBadgeOptions();
    }

    ngOnDestroy(): void {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
    }

    onCancel(): void {}

    onDelete(): void {}

    submitAndValidate(): boolean {
        if (this.form) {
            this.submit();
            return this.valid;
        }
        return true;
    }

    toggleEditCompanySettingsInformation(): void {
        this.companySettingsEditMode = !this.companySettingsEditMode;
    }

    toggleEditTaxFormContactInformation(): void {
        this.companyTaxFormEditMode = !this.companyTaxFormEditMode;
    }

    cancelTaxFormSettingsEdit(): void {
        this.toggleEditTaxFormContactInformation();
        this.settings = { ...this.originalSettings };
        Object.values(this.settings).forEach((value: any) => value instanceof Model && value.revert());
    }

    cancelCompanySettingsEdit(): void {
        this.toggleEditCompanySettingsInformation();
        this.settings = { ...this.originalSettings };
        Object.values(this.settings).forEach((value: any) => value instanceof Model && value.revert());
    }

    async onSave(): Promise<void> {
        this.submit();

        if (!this.valid) {
            return;
        }

        this.isLoading = true;

        try {
            await this.settingsService.save();
            await this.settingsService.reload();

            if (this.isTaxFormContactDialogOpen) {
                this.closeTaxFormContactDialog();
            }

            if (this.isRoeContactDialogOpen) {
                this.closeRoeContactDialog();
            }

            this.notify.success('saved');
        } catch (e) {
            this.notify.error(ErrorParser.parse(e as ParsableError));
            this.isLoading = false;
        } finally {
            this.isLoading = false;
        }
        if (this.companySettingsEditMode) {
            this.toggleEditCompanySettingsInformation();
        }
        if (this.companyTaxFormEditMode) {
            this.toggleEditTaxFormContactInformation();
        }
    }

    getWorkersCompensationBadgeOptions(): BadgeOptions {
        if (this.canActivateWorkersCompensation) {
            return {
                label: 'payroll.settings.wcbPillNotActive',
                type: 'default',
            };
        }

        return {
            label: 'payroll.settings.wcbPillActive',
            type: 'success',
        };
    }

    openActivateWorkersCompensationModal(): void {
        this.isActivateWorkersCompensationModalOpen = true;
    }

    closeActivateWorkersCompensationModal(): void {
        this.isActivateWorkersCompensationModalOpen = false;
    }

    checkIfWorkersCompActivated(): boolean {
        return this.settings?.wcbActivated === null || !this.settings?.wcbActivated;
    }

    async onActivateWorkersCompensation(): Promise<void> {
        if (!this.settings) return;

        this.isLoading = true;

        try {
            this.settings.wcbActivated = true;
            await this.settingsService.save();
            await this.settingsService.reload();
            this.canActivateWorkersCompensation = this.checkIfWorkersCompActivated();
            this.workersCompensationBadgeOptions = this.getWorkersCompensationBadgeOptions();
        } catch (e) {
            this.notify.error(ErrorParser.parse(e as ParsableError));
            this.isLoading = false;
        }

        this.closeActivateWorkersCompensationModal();
    }
}
